import { z } from 'zod';
import { OrderStatus } from '../../screens/Orders/utils';
import { PaginationMetadataSchema } from 'api/common-schemas';
import ResponseSchema from 'screens/OpenQuote/QuoteDetails/schema';
import {
  ProductLocationSchema,
  ProductOfferingSchema,
} from 'screens/SelectServices/schemas';

export const OrderItemSchema = z.object({
  address: z.string(),
  business_name: z.string(),
  site_name: z.string(),
  city: z.string(),
  date_confirmed: z.string(),
  date_ordered: z.string(),
  g360_status: z.nativeEnum(OrderStatus),
  order_number: z.string(),
  product_name: z.string(),
  zip: z.string(),
});

export const OrderItemLocationSchema = z.object({
  business_name: z.string(),
  created_date: z.string(),
  g360_status: z.nativeEnum(OrderStatus),
  id: z.string(),
  order: z.object({
    location_address: z.string(),
    location_city: z.string(),
    location_state: z.string(),
    location_zip: z.string(),
    order_number: z.string(),
  }),
  order_number: z.string(),
  percentage: z.number(),
  product_name: z.string(),
  salesforce_status: z.string(),
});

export const OrdersByLocationSchema = z.object({
  address: z.string(),
  city: z.string(),
  melissa_data_key: z.string(),
  num_services: z.number(),
  orders_items: z.array(OrderItemLocationSchema),
  overall_status: z.nativeEnum(OrderStatus),
  percentage: z.number(),
  site_name: z.string(),
  state: z.string(),
  zip_code: z.string(),
});

export const OrdersByLocationSearchParamsSchema = z
  .object({
    // filters
    tab: z.enum(['all', 'in_progress', 'completed']),
    search: z.string(),

    filter_site_name: z.string(),
    filter_status: z.string(),
    filter_service_type: z.string(),
    // sorting
    sort_by: z.enum(['location', 'status', 'site_name', 'num_services']),
    sort_order: z.enum(['asc', 'desc']),
    // pagination
    page: z.number(),
    page_size: z.number(),
  })
  .partial();

export const ActiveOrderGraphSchema = z.object({
  status: z.string(),
  count: z.number(),
  percentage: z.number(),
});

export const ActiveOrdersGraphSchema = z.object({
  total: z.number(),
  statuses: z.array(ActiveOrderGraphSchema),
});

export type OrdersByLocationSearchParams = z.infer<
  typeof OrdersByLocationSearchParamsSchema
>;

const OrdersExtraMetadataSchema = PaginationMetadataSchema.extend({
  site_names: z.array(z.string()),
  service_types: z.array(z.string()),
  statuses: z.array(z.string()),
});

const OrdersPaginationSchema = z.object({
  metadata: OrdersExtraMetadataSchema,
  data: z.array(OrderItemSchema),
});

const OrdersByLocationPaginationSchema = z.object({
  metadata: OrdersExtraMetadataSchema,
  data: z.array(OrdersByLocationSchema),
});

export type OrdersExtraMetadataType = z.infer<typeof OrdersExtraMetadataSchema>;

export type OrdersByLocationType = z.infer<typeof OrdersByLocationSchema>;
export type OrderItemLocationType = z.infer<typeof OrderItemLocationSchema>;
export type OrderItemType = z.infer<typeof OrderItemSchema>;

export type OrdersByLocationAPIResponse = z.infer<
  typeof OrdersByLocationPaginationSchema
>;
export type OrdersIndexAPIResponse = z.infer<typeof OrdersPaginationSchema>;

export type ActiveOrderGraphType = z.infer<typeof ActiveOrderGraphSchema>;
export type ResponseActiveOrdersGraphType = z.infer<
  typeof ActiveOrdersGraphSchema
>;

export const CalendarEventSchema = z.object({
  time: z.string().datetime(),
  product_name: z.string().nullable(),
  address: z.string().nullable(),
  label: z.string(),
  old_value: z.string().nullable(),
  new_value: z.string().nullable(),
  scheduled_install_date: z.string().nullable(),
  scheduled_install_time: z.string().nullable(),
  vendor: z.string().nullable(),
  site_name: z.string().nullable(),
});

export const CalendarDayGroupSchema = z.object({
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  events: z.array(CalendarEventSchema),
  relative_label: z.string().nullable(),
});

export const CalendarEventsResponseSchema = z.object({
  days: z.array(CalendarDayGroupSchema),
});

export type CalendarEvent = z.infer<typeof CalendarEventSchema>;
export type CalendarDayGroup = z.infer<typeof CalendarDayGroupSchema>;
export type CalendarEventsResponse = z.infer<
  typeof CalendarEventsResponseSchema
>;

export const RecentUpdatesLocationSchema = z.object({
  address: z.string().nullable(),
  city: z.string().nullable(),
  state: z.string().nullable(),
  order_number: z.string().nullable(),
  zip: z.string().nullable(),
});

export const RecentUpdatesDetailsSchema = z.object({
  product_type: z.string(),
  location: RecentUpdatesLocationSchema,
  est_foc_or_scheduled_install_date: z.string().nullable(),
  vendor: z.string().nullable(),
  lec_circuit_id: z.string().nullable(),
  network_or_ip_address: z.string().nullable(),
  broadcast_or_mac_address: z.string().nullable(),
  gateway_or_monitored_ip: z.string().nullable(),
  broadcast_address_or_modem_sn: z.string().nullable(),
  subnet_mask: z.string().nullable(),
  order_status: z.string().nullable(),
  foc_date: z.string().nullable(),
  shipped_date: z.string().nullable(),
  circuit_install_status: z.string().nullable(),
  site_survey_date: z.string().nullable(),
  install_date: z.string().nullable(),
  usable_ip_range: z.string().nullable(),
  dns1: z.string().nullable(),
  dns2: z.string().nullable(),
  modem_make_or_model: z.string().nullable(),
  tracking_id: z.string().nullable(),
  expected_delivery_date: z.string().nullable(),
  cancelation_reason: z.string().nullable(),
});

export const RecentUpdateItemSchema = z.object({
  details: RecentUpdatesDetailsSchema,
  created_date: z.string(),
  new_status: z.string().nullable(),
  type: z.string(),
  product_category: z.string(),
});

export type RecentUpdateItem = z.infer<typeof RecentUpdateItemSchema>;
export type RecentUpdatesDetails = z.infer<typeof RecentUpdatesDetailsSchema>;
export type RecentUpdatesLocation = z.infer<typeof RecentUpdatesLocationSchema>;

export const LocationWithBestOfferingsSchema = ProductLocationSchema.extend({
  best_offerings: z.array(ProductOfferingSchema).default([]),
});

export const QuoteRequestFormalOfferingsWithBestOptionsSchema =
  ResponseSchema.extend({
    id: z.string().uuid(),
    invoice_upload_id: z.string().uuid().nullable(),
    locations: z.array(LocationWithBestOfferingsSchema).default([]),
    qe_quote_created_at: z.string().nullable(),
  });

export type QuoteRequestFormalOfferingsWithBestOptions = z.infer<
  typeof QuoteRequestFormalOfferingsWithBestOptionsSchema
>;

export const OriginalInvoiceQuoteSchema = z.object({
  total_mrc: z.number().nullable().optional(),
  download_speed: z.string().nullable().optional(),
  static_ip: z.boolean().default(false).nullable().optional(),
  network_monitoring: z.boolean().default(false).nullable().optional(),
});

export const InvoiceComparisonQuotesSchema = z.object({
  granite_quote: QuoteRequestFormalOfferingsWithBestOptionsSchema,
  original_quote: OriginalInvoiceQuoteSchema,
});

export type OriginalInvoiceQuote = z.infer<typeof OriginalInvoiceQuoteSchema>;
export type InvoiceComparisonQuotes = z.infer<
  typeof InvoiceComparisonQuotesSchema
>;
